import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Button,
  Checkbox,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import {
  useLazyGetAllPaymentRequestQuery,
  useLazyGetTutorEarningByIdQuery,
  usePutupdateWithdrawStatusMutation,
  useLazyGetApprovedPaymentRequestQuery,
  useLazyGetRejectedPaymentRequestQuery,
} from "../../services/tutor";
import DeleteIcon from "@mui/icons-material/Delete";
import Pagination from "../../components/Pagination";
import Loader from "../../helpers/Loader";
import { errorToast, successToast } from "../../helpers/toast";
import { isValidInput } from "../../utils/validation";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { socket } from "../../config/socket";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const ManagePaymentRequest = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>([]);
  const { _id } = useParams<{ _id: string }>();
  const [fetchApi] = useLazyGetAllPaymentRequestQuery();
  const [approved] = useLazyGetApprovedPaymentRequestQuery();
  const [rejected] = useLazyGetRejectedPaymentRequestQuery();
  const [approvedRows, setApprovedRows] = useState<any>([]);
  const [rejectedRows, setRejectedRows] = useState<any>([]);
  const [updateWithdrawStatus, { isLoading: isUpdating }] =
    usePutupdateWithdrawStatusMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [rows, setRows] = useState<any[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalCount2, setTotalCount2] = useState<number>(0);
  const [totalCount3, setTotalCount3] = useState<number>(0);
  const totalPages = Math.ceil(totalCount / 10);
  const totalPages2 = Math.ceil(totalCount2 / 10);
  const totalPages3 = Math.ceil(totalCount3 / 10);
  const [fetchApiById] = useLazyGetTutorEarningByIdQuery();

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setRows([]);
    setApprovedRows([]);
    setRejectedRows([]);
    setValue(newValue);
    setPage(1);
  };

  useEffect(() => {
    fetchData(page, debouncedSearchTerm);
  }, [page, debouncedSearchTerm]);

  const handleWithdrawStatus = async (rowIds: string[], status: string) => {
    const rowsToUpdate = rows?.filter((r) => rowIds?.includes(r?._id));
    const invalidRows = rowsToUpdate.filter((r) => r.withdraw === 0);

    if (invalidRows.length > 0) {
      errorToast("One or more selected requests have 0 amount.");
      return;
    }

    try {
      const body = {
        ids: rowIds,
        status: status,
      };
      const encryptedBody = {
        encryptedBody: generateEncryptedKeyBody(body) as CommonBody,
      };

      const response = await updateWithdrawStatus(encryptedBody).unwrap();

      if (response?.statusCode === 200) {
        successToast(
          response?.data?.message || "Withdraw status updated successfully"
        );
        fetchData(page, searchTerm);
        fetchApprovedData(page, searchTerm);
        fetchRejecteddData(page, searchTerm);
        setSelectedRows([]);
        setSelectAll(false);
      }
    } catch (e: any) {
      errorToast(e?.message);
    }
  };

  const fetchData = async (page: number, search: string) => {
    setIsLoading(true);
    try {
      const response = await fetchApi({ page, search }).unwrap();
      if (response.statusCode === 200) {
        const formattedRows = response.data.data?.map((item: any) => ({
          _id: item._id,
          tutorDetails: item.tutorId,
          withdraw: item.withdraw,
          withdrawMode: item.withdrawMode,
          createdAt: item.createdAt,
          balance: item.tutorId?.balance || 0,
        }));
        setRows(formattedRows);
        setTotalCount(response.data.total);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApprovedData = async (page: number, search: string) => {
    setIsLoading(true);
    try {
      const response = await approved({ page, search }).unwrap();
      if (response.statusCode === 200) {
        const formattedRows = response.data.data.map((item: any) => ({
          _id: item._id,
          tutorDetails: item.tutorId,
          withdraw: item.withdraw,
          withdrawMode: item.withdrawMode,
          createdAt: item.createdAt,
          balance: item.tutorId?.balance || 0,
        }));
        setApprovedRows(formattedRows);
        setTotalCount2(response?.data?.total);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRejecteddData = async (page: number, search: string) => {
    setIsLoading(true);
    try {
      const response = await rejected({ page, search }).unwrap();
      if (response.statusCode === 200) {
        const formattedRows = response.data.data.map((item: any) => ({
          _id: item._id,
          tutorDetails: item.tutorId,
          withdraw: item.withdraw,
          withdrawMode: item.withdrawMode,
          createdAt: item.createdAt,
          balance: item.tutorId?.balance || 0,
        }));
        setRejectedRows(formattedRows);
        setTotalCount3(response.data.total);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
    setRows([]);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    setSelectedRows(event.target.checked ? rows.map((r) => r?._id) : []);
  };

  const handleRowCheckboxClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    rowId: string
  ) => {
    if (event.target.checked) {
      setSelectedRows((prevSelected) => [...prevSelected, rowId]);
    } else {
      setSelectedRows((prevSelected) =>
        prevSelected.filter((id) => id !== rowId)
      );
    }
  };

  const handleApprove = async () => {
    if (selectedRows.length === 0) {
      errorToast("Please select at least one row to approve.");
      return;
    }
    await handleWithdrawStatus(selectedRows, "accept");
  };

  const handleReject = async () => {
    if (selectedRows.length === 0) {
      errorToast("Please select at least one row to reject.");
      return;
    }
    await handleWithdrawStatus(selectedRows, "reject");
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0) {
      errorToast("Please select at least one row to delete.");
      return;
    }
    await handleWithdrawStatus(selectedRows, "delete");
  };

  const getWithdrawMode = (withdrawMode: number) => {
    switch (withdrawMode) {
      case 1:
        return "Bank";
      case 2:
        return "Mobile money";
      default:
        return "-";
    }
  };

  useEffect(() => {
    if (value === 0) {
      fetchData(page, debouncedSearchTerm);
    } else if (value === 1) {
      fetchApprovedData(page, debouncedSearchTerm);
    } else if (value === 2) {
      fetchRejecteddData(page, debouncedSearchTerm);
    }
  }, [page, debouncedSearchTerm, value]);

  useEffect(() => {
    const handleSocketEvent = (data: any) => {
      if (value === 0) {
        fetchData(page, debouncedSearchTerm);
      } else if (value === 1) {
        fetchApprovedData(page, debouncedSearchTerm);
      } else if (value === 2) {
        fetchRejecteddData(page, debouncedSearchTerm);
      }
    };

    socket?.on("withdrawRequest", handleSocketEvent);

    return () => {
      socket?.off("withdrawRequest", handleSocketEvent);
    };
  }, [value, page, debouncedSearchTerm]);

  return (
    <MainContainer>
      <Loader isLoad={isLoading || isUpdating} />
      <div className="main_layout">
        <div className="dashboard">
          <h2 className="mn_hdng">Payment Request</h2>
        </div>
        <Card className="cards">
          <Box className="custom_tabs">
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Pending" {...a11yProps(0)} />
              <Tab label="Approved" {...a11yProps(1)} />
              <Tab label="Rejected" {...a11yProps(2)} />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <Box className="cards_header">
              <SearchBar
                value={searchTerm}
                searchTerm={searchTerm}
                onCross={() => setSearchTerm("")}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
              <Box className="cards_header_right">
                <div style={{ display: "flex", marginBottom: "10px" }}>
                  {value === 0 && (
                    <>
                      <Button
                        variant="contained"
                        style={{
                          backgroundColor: "#008000",
                          color: "#ffffff",
                          paddingLeft: 10,
                        }}
                        onClick={handleApprove}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="contained"
                        style={{
                          backgroundColor: "black",
                          color: "#ffffff",
                          marginLeft: "10px",
                        }}
                        onClick={handleReject}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    variant="contained"
                    style={{
                      backgroundColor: "grey",
                      color: "#ffffff",
                      marginLeft: "10px",
                    }}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                </div>
              </Box>
            </Box>
            <TableContainer className="table_container">
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAllClick}
                        inputProps={{ "aria-label": "select all rows" }}
                      />
                    </TableCell>
                    <TableCell align="center">S.No</TableCell>
                    <TableCell>Tutor Details</TableCell>
                    <TableCell>Phone No</TableCell>
                    <TableCell>Withdraw Amount</TableCell>
                    <TableCell>Withdraw Mode</TableCell>
                    <TableCell>Net Balance</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell align="center">Request</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No payment request found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => (
                      <TableRow key={row._id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedRows.includes(row?._id)}
                            onChange={(event) =>
                              handleRowCheckboxClick(event, row?._id)
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          {(page - 1) * 10 + index + 1}
                        </TableCell>
                        <TableCell>
                          <div>{row?.tutorDetails?.name}</div>
                          <div>{row?.tutorDetails?.email || "-"}</div>
                        </TableCell>
                        <TableCell>
                          {row.tutorDetails?.phoneNo || "-"}
                        </TableCell>
                        <TableCell align="center">
                          {row?.withdraw > 0 ? row.withdraw : "-"}
                        </TableCell>
                        <TableCell align="center">
                          {getWithdrawMode(row.withdrawMode)}
                        </TableCell>
                        <TableCell>{row?.balance?.toFixed(2) || "-"}</TableCell>
                        <TableCell>
                          {row?.createdAt ? (
                            <>
                              <div>
                                {new Date(row?.createdAt).toLocaleDateString()}
                              </div>
                              <div>
                                {new Date(row?.createdAt).toLocaleTimeString()}
                              </div>
                            </>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            style={{
                              backgroundColor: "#008000",
                              color: "#ffffff",
                            }}
                            onClick={() => {
                              handleWithdrawStatus([row?._id], "accept");
                            }}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="contained"
                            style={{
                              backgroundColor: "black",
                              color: "#ffffff",
                              marginLeft: "10px",
                            }}
                            onClick={() =>
                              handleWithdrawStatus([row?._id], "reject")
                            }
                          >
                            Reject
                          </Button>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() =>
                              handleWithdrawStatus([row?._id], "delete")
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {rows.length > 0 && (
              <Pagination
                setPage={setPage}
                module={rows}
                page={page}
                onPageChange={onPageChange}
                totalPages={totalPages}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />

            <TableContainer className="table_container">
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">S.No</TableCell>
                    <TableCell>Tutor Details</TableCell>
                    <TableCell>Phone No</TableCell>
                    <TableCell>Withdraw Amount</TableCell>
                    <TableCell>Withdraw Mode</TableCell>
                    <TableCell>Net Balance</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No payment request found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    approvedRows.map((row: any, index: any) => (
                      <TableRow key={row._id}>
                        <TableCell align="center">
                          {(page - 1) * 10 + index + 1}
                        </TableCell>
                        <TableCell>
                          <div>{row?.tutorDetails?.name}</div>
                          <div>{row?.tutorDetails?.email || "-"}</div>
                        </TableCell>
                        <TableCell>
                          {row?.tutorDetails?.phoneNo || "-"}
                        </TableCell>
                        <TableCell align="center">
                          {row?.withdraw > 0 ? row.withdraw : "-"}
                        </TableCell>
                        <TableCell align="center">
                          {getWithdrawMode(row.withdrawMode)}
                        </TableCell>
                        <TableCell>{row?.balance?.toFixed(2) || "-"}</TableCell>
                        <TableCell>
                          {row?.createdAt ? (
                            <>
                              <div>
                                {new Date(row?.createdAt).toLocaleDateString()}
                              </div>
                              <div>
                                {new Date(row?.createdAt).toLocaleTimeString()}
                              </div>
                            </>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() =>
                              handleWithdrawStatus([row?._id], "delete")
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {approvedRows.length > 0 && (
              <Pagination
                setPage={setPage}
                module={approvedRows}
                page={page}
                onPageChange={onPageChange}
                totalPages={totalPages2}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <TableContainer className="table_container">
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">S.No</TableCell>
                    <TableCell>Tutor Details</TableCell>
                    <TableCell>Phone No</TableCell>
                    <TableCell>Withdraw Amount</TableCell>
                    <TableCell>Withdraw Mode</TableCell>
                    <TableCell>Net Balance</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rejectedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No payment request found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rejectedRows.map((row: any, index: any) => (
                      <TableRow key={row._id}>
                        <TableCell align="center">
                          {(page - 1) * 10 + index + 1}
                        </TableCell>
                        <TableCell>
                          <div>{row?.tutorDetails?.name}</div>
                          <div>{row?.tutorDetails?.email || "-"}</div>
                        </TableCell>
                        <TableCell>
                          {row?.tutorDetails?.phoneNo || "-"}
                        </TableCell>
                        <TableCell align="center">
                          {row?.withdraw > 0 ? row.withdraw : "-"}
                        </TableCell>
                        <TableCell align="center">
                          {getWithdrawMode(row.withdrawMode)}
                        </TableCell>
                        <TableCell>{row?.balance?.toFixed(2) || "-"}</TableCell>
                        <TableCell>
                          {row?.createdAt ? (
                            <>
                              <div>
                                {new Date(row?.createdAt).toLocaleDateString()}
                              </div>
                              <div>
                                {new Date(row?.createdAt).toLocaleTimeString()}
                              </div>
                            </>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() =>
                              handleWithdrawStatus([row?._id], "delete")
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {rejectedRows.length > 0 && (
              <Pagination
                setPage={setPage}
                module={rejectedRows}
                page={page}
                onPageChange={onPageChange}
                totalPages={totalPages3}
              />
            )}
          </CustomTabPanel>
        </Card>
      </div>
    </MainContainer>
  );
};

export default ManagePaymentRequest;
